/**
 * Intake Wizard Component
 *
 * Multi-step wizard for creating/editing intake requests
 * Features: conditional logic, auto-save, progress tracking
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import WizardStep from './WizardStep';
import ProgressIndicator from './ProgressIndicator';
import BreakingChangeDraftModal from './BreakingChangeDraftModal';
import EAPrefillModal from './EAPrefillModal';
import GAPrefillModal from './GAPrefillModal';
import BCReferenceModal from './BCReferenceModal';
import BCPrefillModal from './BCPrefillModal';
import useAutoSave from '../../hooks/useAutoSave';
import {
  filterVisibleQuestions,
  validateRequiredFields,
  calculateCompletionPercentage,
} from '../../utils/conditionalLogic';
import api from '../../services/api';
import { ROUTES } from '../../config/constants';
import { TYPE_CODES, GPR_SUBTYPE_CODES, GPR_SUBTYPE_ANSWER_TO_CODE, TYPE_DISPLAY_NAMES, TYPE_VERIFIED_DISPLAY_OVERRIDE } from '../../config/requestTypeConstants';
import './IntakeWizard.css';
import './BreakingChangeDraftModal.css';

const APPROVAL_FLOW_TYPES = new Set([
  TYPE_CODES.BREAKING_CHANGE,
  TYPE_CODES.CIC,
  TYPE_CODES.PPP,
  TYPE_CODES.PRIVACY,
  TYPE_CODES.SOR,
]);

const IntakeWizard = ({ requestId, requestType, gprSubtype, initialData, questions, currentUser }) => {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(0);
  // Initialize answers with gpr_subtype if provided (needed for conditional logic)
  const [answers, setAnswers] = useState(() => {
    const initialAnswers = { ...(initialData?.answers || {}) };
    if (gprSubtype && !initialAnswers.gpr_subtype) {
      initialAnswers.gpr_subtype = gprSubtype;
    }
    return initialAnswers;
  });
  const [requestData, setRequestData] = useState({
    product_name: initialData?.product_name || '',
    owner_id: initialData?.owner_id || null,
    location: initialData?.location || '',
    work_areas: initialData?.work_areas || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [skiReleases, setSkiReleases] = useState([]);
  const [showBCModal, setShowBCModal] = useState(false);
  const [showGPRVerifyModal, setShowGPRVerifyModal] = useState(false);
  const [showNPRConfirmModal, setShowNPRConfirmModal] = useState(false);
  const [showEAPrefillModal, setShowEAPrefillModal] = useState(false);
  const [eaPrefillData, setEaPrefillData] = useState(null);
  const eaPrefillCheckedProduct = useRef(null);
  const [showGAPrefillModal, setShowGAPrefillModal] = useState(false);
  const [gaPrefillData, setGaPrefillData] = useState(null);
  const gaPrefillCheckedProduct = useRef(null);
  const [lastEmailSent, setLastEmailSent] = useState(null); // { sentAt, subject }
  const [showBCRefBanner, setShowBCRefBanner] = useState(false);
  const [showBCRefModal, setShowBCRefModal] = useState(false);
  const [bcRefData, setBcRefData] = useState(null);
  const bcRefCheckedKey = useRef(null); // tracks last checked "productName|skiRelease"
  const [showBCPrefillModal, setShowBCPrefillModal] = useState(false);
  const [bcPrefillData, setBcPrefillData] = useState(null);
  const bcPrefillCheckedKey = useRef(null); // tracks last checked "productName|skiRelease" for CiC prefill
  const contentRef = useRef(null);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Scroll to top when an error is set
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  // Resolve effective type code from prop or initialData
  const effectiveTypeCode = requestType || initialData?.requestType?.type_code;

  // Can this user save as Verified for approval-flow types?
  const userRole = currentUser?.role;
  const userApproverTypes = currentUser?.approverTypes || currentUser?.approver_types || [];
  const canVerify = !APPROVAL_FLOW_TYPES.has(effectiveTypeCode) ||
    userRole === 'admin' ||
    userRole === 'developer' ||
    (userRole === 'approver' && userApproverTypes.includes(effectiveTypeCode));

  // Detect request types using type codes
  const isBreakingChange    = effectiveTypeCode === TYPE_CODES.BREAKING_CHANGE;
  const isCIC               = effectiveTypeCode === TYPE_CODES.CIC;
  const isPPP               = effectiveTypeCode === TYPE_CODES.PPP;
  const isPrivacy           = effectiveTypeCode === TYPE_CODES.PRIVACY;
  const isSalesOps          = effectiveTypeCode === TYPE_CODES.SOR;
  const isPricingPackaging  = effectiveTypeCode === TYPE_CODES.PRICING_PACKAGING;
  const isGPRSubType    = GPR_SUBTYPE_CODES.has(effectiveTypeCode);
  const isCUC           = effectiveTypeCode === TYPE_CODES.CUC;
  const isNPR           = effectiveTypeCode === TYPE_CODES.NEW_PRODUCT_REQUEST;

  // Group questions into steps dynamically based on sections
  const steps = React.useMemo(() => {
    if (questions.length === 0) return [];

    // Check if questions have sections (EA Service uses sections)
    const hasSections = questions.some(q => q.section);

    if (hasSections) {
      // Group by section for EA Service and similar forms
      const sectionMap = new Map();
      const sectionOrder = new Map();

      questions.forEach(q => {
        const section = q.section || 'Questions';
        if (!sectionMap.has(section)) {
          sectionMap.set(section, []);
          sectionOrder.set(section, q.display_order);
        }
        sectionMap.get(section).push(q.display_order);
      });

      // Sort sections by their first question's display order
      const sortedSections = Array.from(sectionMap.entries())
        .sort((a, b) => sectionOrder.get(a[0]) - sectionOrder.get(b[0]));

      return sortedSections.map(([title, orders]) => ({
        title,
        sectionName: title,
        questionRange: [Math.min(...orders), Math.max(...orders)]
      }));
    } else {
      // Default step grouping for forms without sections
      return [
        { title: 'Basic Information', questionRange: [1, 5] },
        { title: 'Update Details', questionRange: [6, 10] },
        { title: 'Testing & Delivery', questionRange: [11, 15] },
        { title: 'Effort Estimation', questionRange: [16, 20] },
      ];
    }
  }, [questions]);

  // Filter questions for a given step — uses section name when available to avoid
  // display_order range collisions across sections.
  const getStepQuestions = React.useCallback((step) => {
    if (!step) return [];
    if (step.sectionName) {
      return questions.filter(q => q.section === step.sectionName);
    }
    const [start, end] = step.questionRange;
    return questions.filter(q => q.display_order >= start && q.display_order <= end);
  }, [questions]);

  // Get questions for current step
  const currentStepQuestions = steps.length === 0 ? [] : getStepQuestions(steps[currentStep]);

  // Filter visible questions based on answers
  const visibleQuestions = filterVisibleQuestions(currentStepQuestions, answers);

  // Find the nearest step in a given direction (+1/-1) that has visible questions
  const getNextVisibleStep = React.useCallback((from, direction) => {
    let next = from + direction;
    while (next >= 0 && next < steps.length) {
      const stepQs = getStepQuestions(steps[next]);
      if (filterVisibleQuestions(stepQs, answers).length > 0) return next;
      next += direction;
    }
    return from;
  }, [steps, answers, getStepQuestions]);

  // True when at least one later step still has visible questions
  const hasMoreVisibleSteps = React.useMemo(() => {
    for (let i = currentStep + 1; i < steps.length; i++) {
      const stepQs = getStepQuestions(steps[i]);
      if (filterVisibleQuestions(stepQs, answers).length > 0) return true;
    }
    return false;
  }, [currentStep, steps, answers, getStepQuestions]);

  // Calculate progress
  const completionPercentage = calculateCompletionPercentage(questions, answers);

  // Update answers when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData?.answers) {
      setAnswers(prev => ({
        ...prev,
        ...initialData.answers
      }));
    }
  }, [initialData]);

  // Update requestData when initialData changes
  useEffect(() => {
    if (initialData) {
      setRequestData({
        product_name: initialData.product_name || '',
        owner_id: initialData.owner_id || null,
        location: initialData.location || '',
        work_areas: initialData.work_areas || '',
      });
    }
  }, [initialData, requestType]);

  // Fetch users for dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.getAllUsers();
        const fetchedUsers = (response.data.data.users || []).map(user => ({
          ...user,
          displayName: `${user.fullName} (${user.email})`
        }));
        setUsers(fetchedUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, []);

  // Fetch products for dropdown
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.getProducts({ status: 'active', limit: 1000 });
        setProducts(response.data.data.products || []);
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };
    fetchProducts();
  }, []);

  // Fetch ski releases for dropdown
  useEffect(() => {
    const fetchSkiReleases = async () => {
      try {
        const response = await api.getSkiReleases();
        setSkiReleases((response.data.data.skiReleases || []).filter(r => r.status !== 'released'));
      } catch (err) {
        console.error('Error fetching ski releases:', err);
      }
    };
    fetchSkiReleases();
  }, []);

  // EA → GA field mapping for pre-fill
  const EA_TO_GA_FIELD_MAP = {
    gpr_ea_product_name:              'gpr_ga_product_name',
    gpr_ea_ski_release:               'gpr_ga_ski_release',
    gpr_ea_owner:                     'gpr_ga_owner',
    gpr_ea_aha_link:                  'gpr_ga_aha_link',
    gpr_ea_slack_channel:             'gpr_ga_slack_channel',
    gpr_ea_purpose:                   'gpr_ga_purpose',
    gpr_ea_datadog_links:             'gpr_ga_datadog_links',
    gpr_ea_draft_runbook:             'gpr_ga_draft_runbook',
    gpr_ea_regions:                   'gpr_ga_regions',
    gpr_ea_arch_diagram:              'gpr_ga_arch_diagram',
    gpr_ea_code_repo:                 'gpr_ga_code_repo',
    gpr_ea_sku:                       'gpr_ga_sku',
    gpr_ea_staging_galileo:           'gpr_ga_staging_galileo',
    gpr_ea_jsm_registered:            'gpr_ga_jsm_registered',
    gpr_ea_sandbox_environment:       'gpr_ga_sandbox_environment',
    gpr_ea_provisioning_escalation:   'gpr_ga_escalation_path',
    gpr_ea_product_docs:              'gpr_ga_product_docs',
    gpr_ea_observability_checklist:   'gpr_ga_observability_checklist',
    gpr_ea_provisioning_kt:           'gpr_ga_provisioning_kt',
  };

  // GA → SalesOps field mapping for pre-fill
  const GA_TO_SALESOPS_FIELD_MAP = {
    gpr_ga_product_name:    'salesops_product_capability_name',
    gpr_ga_ski_release:     'salesops_ski_release',
    gpr_ga_purpose:         'salesops_new_product_description',
    gpr_ga_regions:         'salesops_regional_restrictions',
    gpr_ga_comm_plan:       'salesops_customer_access_communication',
    gpr_ga_provisioning_kt: 'salesops_provisioning_method',
  };

  // Check for existing GPR GA request when BOTH product AND ski release are selected on a new SalesOps request.
  // Only show the prefill modal when the selected ski release matches the GA request's ski release.
  useEffect(() => {
    if (effectiveTypeCode !== TYPE_CODES.SOR || requestId) return;
    const productName = answers.salesops_product_capability_name;
    const skiRelease = answers.salesops_ski_release;

    // Both fields must be present — clear any stale prefill if either is empty
    if (!productName || !skiRelease) {
      setGaPrefillData(null);
      setShowGAPrefillModal(false);
      gaPrefillCheckedProduct.current = null;
      return;
    }

    const checkKey = `${productName}|${skiRelease}`;
    if (checkKey === gaPrefillCheckedProduct.current) return;

    gaPrefillCheckedProduct.current = checkKey;

    api.checkGAPrefill({ product_name: productName })
      .then(res => {
        const { found, gaRequest } = res.data.data;
        if (found && gaRequest.ski_release === skiRelease) {
          setGaPrefillData(gaRequest);
          setShowGAPrefillModal(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          // GA not found or ski release doesn't match — clear any stale prefill
          setGaPrefillData(null);
          setShowGAPrefillModal(false);
        }
      })
      .catch(() => {}); // non-critical — silently ignore
  }, [answers.salesops_product_capability_name, answers.salesops_ski_release, effectiveTypeCode, requestId]);

  // Check for existing EA request when product is selected on a new GPR GA request
  useEffect(() => {
    if (effectiveTypeCode !== TYPE_CODES.GPR_GA || requestId) return;
    const productName = answers.gpr_ga_product_name;
    if (!productName || productName === eaPrefillCheckedProduct.current) return;

    eaPrefillCheckedProduct.current = productName;

    api.checkEAPrefill({ product_name: productName })
      .then(res => {
        const { found, eaRequest } = res.data.data;
        if (found) {
          setEaPrefillData(eaRequest);
          setShowEAPrefillModal(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      })
      .catch(() => {}); // non-critical — silently ignore
  }, [answers.gpr_ga_product_name, effectiveTypeCode, requestId]);

  // Check for a verified BC request when product + ski release are both set on a CuC form
  useEffect(() => {
    if (!isCUC) return;
    const productName = answers.product_capability_name;
    const skiReleaseName = answers.ski_release;
    if (!productName || !skiReleaseName) return;

    const checkKey = `${productName}|${skiReleaseName}`;
    if (checkKey === bcRefCheckedKey.current) return;
    bcRefCheckedKey.current = checkKey;

    api.checkBCForCuC({ product_name: productName, ski_release_name: skiReleaseName })
      .then(res => {
        const { found, bcRequest } = res.data.data;
        if (found) {
          setBcRefData(bcRequest);
          setShowBCRefBanner(true);
        } else {
          setBcRefData(null);
          setShowBCRefBanner(false);
        }
      })
      .catch(() => {}); // non-critical — silently ignore
  }, [answers.product_capability_name, answers.ski_release, isCUC]);

  // Check for in-progress BC (blocks CiC creation) and verified BC (prefill) on new CiC form
  useEffect(() => {
    if (!isCIC || requestId) return; // only on new CiC requests
    const productName = answers.bc_product_name;
    const skiReleaseName = answers.bc_ski_release;

    // Clear any previous BC in-progress blocking error when product/SKI changes
    setError(null);

    if (!productName || !skiReleaseName) return;

    const checkKey = `${productName}|${skiReleaseName}`;
    if (checkKey === bcPrefillCheckedKey.current) return;
    bcPrefillCheckedKey.current = checkKey;

    // First: check for a non-verified (in-progress) BC — blocks CiC creation
    api.checkBCInProgressForCiC({ product_name: productName, ski_release_name: skiReleaseName })
      .then(res => {
        const { found } = res.data.data;
        if (found) {
          setError('A Breaking Change request is in-progress for this product. Cannot create this request at this time.');
          return;
        }
        // No in-progress BC — check for a verified BC to offer prefill
        api.checkBCForCiC({ product_name: productName, ski_release_name: skiReleaseName })
          .then(res => {
            const { found: bcFound, bcRequest } = res.data.data;
            if (bcFound) {
              setBcPrefillData(bcRequest);
              setShowBCPrefillModal(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              setBcPrefillData(null);
            }
          })
          .catch(() => {}); // non-critical — silently ignore
      })
      .catch(() => {}); // non-critical — silently ignore
  }, [answers.bc_product_name, answers.bc_ski_release, isCIC, requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load email history for existing BC / CiC / PPP / Privacy / SalesOps / GPR sub-type requests
  useEffect(() => {
    if (!requestId) return;
    const needsCheck = isBreakingChange || isCIC || isPPP || isPrivacy || isSalesOps || isGPRSubType;
    if (!needsCheck) return;
    api.getEmailHistory(requestId)
      .then(res => {
        const { sent, sentAt, subject } = res.data.data;
        if (sent) setLastEmailSent({ sentAt, subject });
      })
      .catch(() => {}); // silently ignore — non-critical
  }, [requestId, isBreakingChange, isCIC, isPPP, isSalesOps, isGPRSubType]);

  // Auto-populate categorization from product when in edit mode
  useEffect(() => {
    if (
      products.length > 0 &&
      initialData?.answers?.product_capability_name &&
      !initialData?.answers?.categorization
    ) {
      const productName = initialData.answers.product_capability_name;
      const selectedProduct = products.find(p => p.product_name === productName);

      if (selectedProduct && selectedProduct.categorization) {
        setAnswers(prev => ({
          ...prev,
          categorization: selectedProduct.categorization
        }));
      }
    }
  }, [products, initialData]);

  // Auto-save hook
  useAutoSave(requestId, answers, 30000, initialData?.status !== 'Needs Input');

  // Two-status types use Save as Draft / Save as Verified only

  const handleBCPrefillConfirm = () => {
    if (!bcPrefillData) return;
    // CiC and BC share the same bc_* question keys — copy all answers directly
    setAnswers(prev => ({ ...prev, ...bcPrefillData.answers }));
    setShowBCPrefillModal(false);
    setBcPrefillData(null);
  };

  const handleBCPrefillDismiss = () => {
    setShowBCPrefillModal(false);
    setBcPrefillData(null);
  };

  const handleEAPrefillConfirm = () => {
    if (!eaPrefillData) return;
    const prefilled = {};
    Object.entries(EA_TO_GA_FIELD_MAP).forEach(([eaKey, gaKey]) => {
      if (eaPrefillData.answers[eaKey]) {
        prefilled[gaKey] = eaPrefillData.answers[eaKey];
      }
    });
    setAnswers(prev => ({ ...prev, ...prefilled }));
    setShowEAPrefillModal(false);
    setEaPrefillData(null);
  };

  const handleEAPrefillDismiss = () => {
    setShowEAPrefillModal(false);
    setEaPrefillData(null);
  };

  const handleGAPrefillConfirm = () => {
    if (!gaPrefillData) return;
    const prefilled = {};
    Object.entries(GA_TO_SALESOPS_FIELD_MAP).forEach(([gaKey, sorKey]) => {
      if (gaPrefillData.answers[gaKey]) {
        prefilled[sorKey] = gaPrefillData.answers[gaKey];
      }
    });
    setAnswers(prev => ({ ...prev, ...prefilled }));
    setShowGAPrefillModal(false);
    setGaPrefillData(null);
  };

  const handleGAPrefillDismiss = () => {
    setShowGAPrefillModal(false);
    setGaPrefillData(null);
  };

  const handleBCRefApplyReason = (summary) => {
    setAnswers(prev => ({ ...prev, reason: summary }));
  };

  const handleAnswerChange = (questionKey, value) => {
    setAnswers((prev) => {
      const updated = { ...prev, [questionKey]: value };

      // SalesOps: clear the section that doesn't apply when product type changes
      if (questionKey === 'salesops_product_type') {
        const newProductFields = [
          'salesops_pricing_metric', 'salesops_pricing_metric_details',
          'salesops_discountable', 'salesops_purchase_per_xcenter', 'salesops_purchase_per_star',
        ];
        const existingProductFields = [
          'salesops_existing_product_changes', 'salesops_pricing_packaging_impact',
          'salesops_existing_customer_cost',
        ];
        const toClear = value === 'New Commercial Product' ? existingProductFields
          : value === 'Change to Existing Product' ? newProductFields : [];
        toClear.forEach(k => { updated[k] = ''; });
      }

      // NPR: clear the section that doesn't apply when product type changes
      if (questionKey === 'npr_product_type') {
        const commercialFields = [
          'npr_business_unit', 'npr_performance_matrix_group', 'npr_product_family',
          'npr_core_product_type', 'npr_has_cuac', 'npr_based_on',
        ];
        const technicalFields = [
          'npr_ppp_apply', 'npr_provisioning_required', 'npr_provisioning_team',
          'npr_provisioning_automated', 'npr_provisioned_in_npe_prod',
          'npr_has_provisioning_action_on_renewal', 'npr_fulfilment_qty_calc_method', 'npr_polaris_id',
        ];
        const toClear = value === 'COMMERCIAL' ? technicalFields
          : value === 'TECHNICAL' ? commercialFields : [];
        toClear.forEach(k => { updated[k] = ''; });
      }

      // BC/CiC: clear child detail fields when a yes/no parent flips away from 'Yes'
      const BC_YES_CHILDREN = {
        bc_are_there_exceptions:       ['bc_exception_process', 'bc_exception_approver', 'bc_exception_remediation_timeline'],
        bc_contract_remediation:       ['bc_contract_remediation_rationale'],
        bc_perceived_loss_of_function: ['bc_loss_messaging'],
        bc_regional_considerations:    ['bc_regional_rationale'],
        bc_l3_approved:                ['bc_l3_name'],
      };
      if (BC_YES_CHILDREN[questionKey] && value !== 'Yes') {
        BC_YES_CHILDREN[questionKey].forEach(k => { updated[k] = ''; });
      }

      // PPP: clear npe_type when capability_ga is no longer 'No'
      if (questionKey === 'ppp_capability_ga' && value !== 'No') {
        updated['ppp_npe_type'] = '';
      }

      // Privacy: clear individual explain fields when their parent flips to non-triggering value
      const PRIVACY_EXPLAIN_MAP = {
        privacy_pii_not_in_logs:    { trigger: 'No',  children: ['privacy_pii_not_in_logs_explain'] },
        privacy_region_restricted:  { trigger: 'No',  children: ['privacy_region_restricted_explain'] },
        privacy_pii_erasable:       { trigger: 'No',  children: ['privacy_pii_erasable_explain'] },
        privacy_customer_retention: { trigger: 'No',  children: ['privacy_customer_retention_explain'] },
        privacy_pii_exportable:     { trigger: 'No',  children: ['privacy_pii_exportable_explain'] },
        privacy_automated_decisions:{ trigger: 'Yes', children: ['privacy_automated_decisions_explain'] },
        privacy_privacy_by_design:  { trigger: 'No',  children: ['privacy_privacy_by_design_explain'] },
        privacy_pseudonymization:   { trigger: 'No',  children: ['privacy_pseudonymization_explain'] },
        privacy_do_not_process:     { trigger: 'No',  children: ['privacy_do_not_process_explain'] },
        privacy_external_vendors:   { trigger: 'Yes', children: ['privacy_external_vendors_details'] },
        privacy_third_party_data:   { trigger: 'Yes', children: ['privacy_third_party_data_details'] },
      };
      if (PRIVACY_EXPLAIN_MAP[questionKey]) {
        const { trigger, children } = PRIVACY_EXPLAIN_MAP[questionKey];
        if (value !== trigger) children.forEach(k => { updated[k] = ''; });
      }

      // Privacy: clear all PII-gated fields when all three PII trigger questions become non-Yes
      const PII_TRIGGER_KEYS = ['privacy_pii_collected', 'privacy_new_data_source', 'privacy_architecture_change'];
      if (PII_TRIGGER_KEYS.includes(questionKey)) {
        const anyStillTriggered = PII_TRIGGER_KEYS.some(k => (k === questionKey ? value : prev[k]) === 'Yes');
        if (!anyStillTriggered) {
          [
            'privacy_pii_description', 'privacy_pii_purpose', 'privacy_pii_environments',
            'privacy_pii_not_in_logs', 'privacy_pii_not_in_logs_explain',
            'privacy_region_restricted', 'privacy_region_restricted_explain',
            'privacy_pii_erasable', 'privacy_pii_erasable_explain',
            'privacy_customer_retention', 'privacy_customer_retention_explain',
            'privacy_pii_exportable', 'privacy_pii_exportable_explain',
            'privacy_automated_decisions', 'privacy_automated_decisions_explain',
            'privacy_privacy_by_design', 'privacy_privacy_by_design_explain',
            'privacy_pseudonymization', 'privacy_pseudonymization_explain',
            'privacy_do_not_process', 'privacy_do_not_process_explain',
            'privacy_external_vendors', 'privacy_external_vendors_details',
            'privacy_third_party_data', 'privacy_third_party_data_details',
          ].forEach(k => { updated[k] = ''; });
        }
      }

      return updated;
    });
  };

  const handleRequestDataChange = (field, value) => {
    setRequestData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Maps each form type (type_code) to the answer key that holds the product name
  const PRODUCT_KEY_BY_TYPE = {
    [TYPE_CODES.CUC]:               'product_capability_name',
    [TYPE_CODES.SOR]:               'salesops_product_capability_name',
    [TYPE_CODES.BREAKING_CHANGE]:   'bc_product_name',
    [TYPE_CODES.CIC]:               'bc_product_name',
    [TYPE_CODES.PPP]:               'ppp_product_name',
    [TYPE_CODES.PRIVACY]:           'privacy_product_name',
    [TYPE_CODES.PRICING_PACKAGING]: 'product_name',
    [TYPE_CODES.GPR_GA]:            'gpr_ga_product_name',
    [TYPE_CODES.GPR_EX]:            'gpr_ex_product_name',
    [TYPE_CODES.GPR_INT]:           'gpr_int_product_name',
    [TYPE_CODES.GPR_MKT]:           'gpr_mkt_product_name',
    [TYPE_CODES.GPR_EA]:            'gpr_ea_product_name',
    [TYPE_CODES.GPR_RUNBOOK]:       'gpr_rb_product_name',
    [TYPE_CODES.NEW_PRODUCT_REQUEST]: 'npr_product_name',
  };

  // Maps each form type (type_code) to the answer key that holds the ski release name
  const SKI_RELEASE_KEY_BY_TYPE = {
    [TYPE_CODES.CUC]:               'ski_release',
    [TYPE_CODES.SOR]:               'salesops_ski_release',
    [TYPE_CODES.BREAKING_CHANGE]:   'bc_ski_release',
    [TYPE_CODES.CIC]:               'bc_ski_release',
    [TYPE_CODES.PPP]:               'ppp_ski_release',
    [TYPE_CODES.PRIVACY]:           'privacy_ski_release',
    [TYPE_CODES.PRICING_PACKAGING]: 'ski_release',
    [TYPE_CODES.GPR_GA]:            'gpr_ga_ski_release',
    [TYPE_CODES.GPR_EX]:            'gpr_ex_ski_release',
    [TYPE_CODES.GPR_INT]:           'gpr_int_ski_release',
    [TYPE_CODES.GPR_MKT]:           'gpr_mkt_ski_release',
    [TYPE_CODES.GPR_EA]:            'gpr_ea_ski_release',
    [TYPE_CODES.GPR_RUNBOOK]:       'gpr_rb_ski_release',
  };

  const handleNext = async () => {
    // Validate current step
    const { isValid, missingFields } = validateRequiredFields(
      visibleQuestions,
      answers
    );

    if (!isValid) {
      setError(
        `Please fill in all required fields: ${missingFields
          .map((f) => f.label)
          .join(', ')}`
      );
      return;
    }

    // On first step: run type-specific validations
    if (currentStep === 0) {
      // NPR: check that the product name and product code don't already exist in the catalog
      if (isNPR) {
        const productName = answers.npr_product_name?.trim();
        const productCode = answers.npr_product_code?.trim();

        if (productName) {
          try {
            setLoading(true);
            const response = await api.checkProductName(productName);
            setLoading(false);
            if (response.data.exists) {
              setError(
                `A product named "${productName}" already exists in the catalog. ` +
                `Please use a different product name or contact Product Operations if you believe this is an error.`
              );
              return;
            }
          } catch (err) {
            console.error('Error checking product name:', err);
            setLoading(false);
          }
        }

        if (productCode) {
          try {
            setLoading(true);
            const response = await api.checkProductCode(productCode);
            setLoading(false);
            if (response.data.exists) {
              setError(
                `Product code "${productCode}" is already used by "${response.data.productName}". ` +
                `Please enter a different product code.`
              );
              return;
            }
          } catch (err) {
            console.error('Error checking product code:', err);
            setLoading(false);
          }
        }
      } else {
        // Standard duplicate request check for all other types
        const productKey = PRODUCT_KEY_BY_TYPE[effectiveTypeCode];
        const skiKey = SKI_RELEASE_KEY_BY_TYPE[effectiveTypeCode];
        const productName = productKey ? answers[productKey] : null;
        const skiReleaseName = skiKey ? answers[skiKey] : null;

        if (effectiveTypeCode && productName) {
          try {
            setLoading(true);
            const response = await api.checkDuplicate({
              type_code: effectiveTypeCode,
              product_name: productName,
              ...(skiReleaseName ? { ski_release_name: skiReleaseName } : {}),
              ...(requestId ? { exclude_id: requestId } : {}),
            });
            setLoading(false);

            if (response.data.exists) {
              const existing = response.data.data.existingRequest;
              const refNum = existing?.request_number ? ` (${existing.request_number})` : '';
              setError(
                `A request for this product${skiReleaseName ? ' and ski release' : ''} combination already exists${refNum}. ` +
                `Please edit the existing request instead.`
              );
              return;
            }
          } catch (err) {
            console.error('Error checking for duplicate:', err);
            setLoading(false);
            // Don't block the user if the check itself fails
          }
        }
      }
    }

    setError(null);
    setCurrentStep(getNextVisibleStep(currentStep, 1));
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep(getNextVisibleStep(currentStep, -1));
  };

  // Set of step indices that have at least one visible question
  const visibleStepIndices = React.useMemo(() => {
    const result = new Set();
    steps.forEach((step, idx) => {
      const stepQs = getStepQuestions(step);
      if (filterVisibleQuestions(stepQs, answers).length > 0) result.add(idx);
    });
    return result;
  }, [steps, answers, getStepQuestions]);

  const handleStepClick = React.useCallback(async (targetIndex) => {
    if (targetIndex === currentStep) return;
    if (!visibleStepIndices.has(targetIndex)) return;

    // Free backward navigation
    if (targetIndex < currentStep) {
      setError(null);
      setCurrentStep(targetIndex);
      return;
    }

    // Validated forward navigation — validate every step between current and target
    for (let stepIdx = currentStep; stepIdx < targetIndex; stepIdx++) {
      if (!visibleStepIndices.has(stepIdx)) continue;
      const stepQs = getStepQuestions(steps[stepIdx]);
      const visibleQs = filterVisibleQuestions(stepQs, answers);
      const { isValid, missingFields } = validateRequiredFields(visibleQs, answers);
      if (!isValid) {
        setError(
          `Please fill in all required fields on "${steps[stepIdx].title}": ${missingFields
            .map(f => f.label)
            .join(', ')}`
        );
        setCurrentStep(stepIdx);
        return;
      }
    }

    setError(null);
    setCurrentStep(targetIndex);
  }, [currentStep, visibleStepIndices, steps, answers, getStepQuestions]);

  // ── Core save-as-draft logic (shared by BC and non-BC) ────────────────────
  const performSaveDraft = async () => {
    setLoading(true);
    setError(null);
    try {
      if (requestId) {
        const response = await api.updateRequest(requestId, {
          ...requestData,
          answers,
          status: 'Draft',
        });

        // Check if we should refresh with existing record
        if (response.data.data.request._refresh_with_existing) {
          alert(response.data.data.request._refresh_message || 'Refreshed!');
          window.location.href = `/intake/${response.data.data.request.id}`;
          return;
        }

        // Check if a new record was created with verification message
        if (response.data.data.request._verification_message) {
          alert(response.data.data.request._verification_message);
          if (response.data.data.request._is_new_record) {
            history.push(`/intake/${response.data.data.request.id}`);
            return;
          }
        } else if (response.data.data.request._redirect_to_latest) {
          alert('Draft saved successfully! Redirecting to the most recent record.');
          history.push(`/intake/${response.data.data.request.id}`);
          return;
        } else {
          history.push(ROUTES.DASHBOARD);
          return;
        }
      } else {
        // Create new request — always saved as draft
        await api.createRequest({
          ...requestData,
          type_code: effectiveTypeCode,
          answers,
        });
        history.push(ROUTES.DASHBOARD);
        return;
      }
      history.push(ROUTES.DASHBOARD);
    } catch (err) {
      console.error('Error saving draft:', err);
      setError(err.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  // ── Save as Draft ─────────────────────────────────────────────────────────
  const handleSaveDraft = () => {
    // If a notification was already sent for this request, skip the modal and just save.
    // The user can re-send via the banner link if needed.
    if ((isBreakingChange || isCIC || isPPP || isPrivacy || isSalesOps) && !lastEmailSent) {
      setShowBCModal(true);
      return;
    }
    performSaveDraft();
  };

  // ── BC modal handlers ──────────────────────────────────────────────────────
  const handleBCModalNotify = async () => {
    setShowBCModal(false);
    setLoading(true);
    setError(null);
    try {
      let savedRequestId = requestId;
      const saveStatus = 'Draft';
      if (requestId) {
        await api.updateRequest(requestId, { ...requestData, answers, status: saveStatus });
      } else {
        const response = await api.createRequest({ ...requestData, type_code: effectiveTypeCode, answers, status: saveStatus });
        savedRequestId = response.data.data.request.id;
      }

      const templateName = isCIC ? 'renderCiCDraft' : isPPP ? 'renderPPPDraft' : isPrivacy ? 'renderPrivacyDraft' : isSalesOps ? 'renderSalesOpsDraft' : isPricingPackaging ? 'renderPricingPackagingDraft' : 'renderBreakingChangeDraft';
      const notifyMsg = isCIC
        ? 'Request saved and CiC Process Owner notified.'
        : isPPP
        ? 'Request saved and PPP Reviewers notified.'
        : isPrivacy
        ? 'Request saved and Privacy Process Owner notified.'
        : isSalesOps
        ? 'Request saved and SalesOps Process Owner notified.'
        : isPricingPackaging
        ? 'Request saved and Pricing & Packaging Owner notified.'
        : 'Request saved and BC Process Owner notified.';
      try {
        await api.sendEmail({
          templateName,
          requestId: savedRequestId,
        });
        alert(notifyMsg);
      } catch (emailErr) {
        console.error('Email notification failed:', emailErr);
        alert('Request saved. Notification failed — will retry automatically.');
      }

      history.push(ROUTES.DASHBOARD);
    } catch (err) {
      console.error('Error saving BC draft:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const handleBCModalJustSave = async () => {
    setShowBCModal(false);
    await performSaveDraft();
  };

  const handleBCModalCancel = () => {
    setShowBCModal(false);
  };

  // ── NPR Submit Request ────────────────────────────────────────────────────
  const handleNPRSubmit = () => {
    // Validate all visible questions before showing confirmation
    const { isValid, missingFields } = validateRequiredFields(questions, answers);
    if (!isValid) {
      setError(
        `Please complete all required fields before submitting: ${missingFields.map(f => f.label).join(', ')}`
      );
      return;
    }
    setShowNPRConfirmModal(true);
  };

  const handleNPRConfirm = async () => {
    setShowNPRConfirmModal(false);
    setLoading(true);
    setError(null);
    try {
      let savedRequestId = requestId;
      if (requestId) {
        await api.updateRequest(requestId, { ...requestData, answers, status: 'Draft' });
      } else {
        const response = await api.createRequest({
          ...requestData,
          type_code: effectiveTypeCode,
          answers,
          status: 'Draft',
        });
        savedRequestId = response.data.data.request.id;
      }

      // Fire Template A — notify Product Ops
      try {
        await api.sendEmail({ templateName: 'renderNewProductRequestOps', requestId: savedRequestId });
      } catch (emailErr) {
        console.error('NPR ops email failed:', emailErr);
        // Non-critical — continue
      }

      alert('Your product request has been submitted. Product Operations will be in touch.');
      history.push(ROUTES.DASHBOARD);
    } catch (err) {
      console.error('Error submitting NPR:', err);
      setError(err.response?.data?.message || err.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  // ── Save as Verified (edit mode only) ─────────────────────────────────────
  const handleSaveVerified = async () => {
    // Validate all questions
    const { isValid, missingFields } = validateRequiredFields(questions, answers);

    if (!isValid) {
      setError(
        `Please complete all required fields before verifying: ${missingFields
          .map((f) => f.label)
          .join(', ')}`
      );
      return;
    }

    // GPR sub-types require a confirmation popup before verifying
    if (isGPRSubType) {
      setShowGPRVerifyModal(true);
      return;
    }

    await performSaveVerified();
  };

  const performSaveVerified = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.updateRequest(requestId, {
        ...requestData,
        answers,
        status: 'Verified',
      });

      alert('Request saved as verified.');
      history.push(ROUTES.DASHBOARD);
    } catch (err) {
      console.error('Error saving as verified:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save as verified');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNPR = async () => {
    if (!window.confirm('Are you sure you want to delete this product request? This cannot be undone.')) return;
    try {
      await api.deleteRequest(requestId);
      history.push(ROUTES.DASHBOARD);
    } catch (err) {
      console.error('Error deleting NPR:', err);
      alert('Failed to delete request: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
      if (requestId) {
        // Editing an existing request — go to dashboard
        history.push(ROUTES.DASHBOARD);
      } else {
        // Creating a new request — go back to the type selector
        history.goBack();
      }
    }
  };

  // Derive current product name from answers or requestData (works for all form types)
  const currentProductName =
    answers.product_capability_name ||
    answers.salesops_product_capability_name ||
    answers.bc_product_name ||
    answers.cic_product_name ||
    answers.ppp_product_name ||
    answers.privacy_product_name ||
    answers.product_name ||
    answers.ea_product_id ||
    answers.gpr_ea_product_name ||
    answers.gpr_rb_product_name ||
    answers.npr_product_name ||
    requestData.product_name ||
    initialData?.product_name ||
    null;

  // Get display name for request type using TYPE_DISPLAY_NAMES
  const getRequestTypeDisplayName = () => {
    if (gprSubtype) return GPR_SUBTYPE_ANSWER_TO_CODE[gprSubtype]
      ? (TYPE_DISPLAY_NAMES[GPR_SUBTYPE_ANSWER_TO_CODE[gprSubtype]] || gprSubtype)
      : gprSubtype;

    // Supplement with wizard-specific labels
    const wizardLabels = {
      [TYPE_CODES.CUC]: 'Update Adoption Criteria Intake',
      [TYPE_CODES.GPR]: 'Product Readiness Intake',
      [TYPE_CODES.PRICING_PACKAGING]: 'Pricing & Packaging Intake',
    };

    return wizardLabels[effectiveTypeCode]
      || TYPE_DISPLAY_NAMES[effectiveTypeCode]
      || effectiveTypeCode
      || 'Product Readiness Intake';
  };

  // Guard: Don't render if steps haven't been initialized yet
  if (steps.length === 0) {
    return (
      <div className="intake-wizard">
        <div className="intake-wizard__loading">
          <div className="intake-wizard__spinner" />
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-wizard">
      {/* NPR Submit Confirmation Modal */}
      {showNPRConfirmModal && (
        <div className="bc-modal__overlay">
          <div className="bc-modal">
            <h2 className="bc-modal__title">Submit Product Request</h2>
            <p className="bc-modal__body">
              By submitting this request, an email will be sent to the Product Operations group
              to review and create this product. Do you wish to continue?
            </p>
            <div className="bc-modal__actions">
              <button
                className="intake-wizard__button intake-wizard__button--primary"
                onClick={handleNPRConfirm}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Yes, Submit Request'}
              </button>
              <button
                className="intake-wizard__button intake-wizard__button--secondary"
                onClick={() => setShowNPRConfirmModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GPR Sub-Type Verify Confirmation Modal */}
      {showGPRVerifyModal && (
        <div className="bc-modal__overlay">
          <div className="bc-modal">
            <h2 className="bc-modal__title">Verify GPR Request</h2>
            <p className="bc-modal__body">
              Verifying this GPR sub-type request will trigger an e-mail to the GPR team
              who will create a GPR, and schedule a kick-off meeting to review the request.
              Confirm?
            </p>
            <div className="bc-modal__actions">
              <button
                className="intake-wizard__button intake-wizard__button--primary"
                onClick={() => { setShowGPRVerifyModal(false); performSaveVerified(); }}
                disabled={loading}
              >
                Yes, Verify
              </button>
              <button
                className="intake-wizard__button intake-wizard__button--secondary"
                onClick={() => setShowGPRVerifyModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BC / CiC / PPP Draft Notification Modal */}
      {showBCModal && (
        <BreakingChangeDraftModal
          onNotify={handleBCModalNotify}
          onJustSave={handleBCModalJustSave}
          onCancel={handleBCModalCancel}
          loading={loading}
          title={isCIC ? 'CiC Request — Internal Assessment Required' : isPPP ? 'PPP Request — Notify Reviewers?' : isPrivacy ? 'Privacy Request — Internal Assessment Required' : isSalesOps ? 'SalesOps Request — Notify Process Owner?' : isPricingPackaging ? 'Pricing & Packaging — Notify Reviewer?' : undefined}
          body={isCIC ? 'Approval of this request is required. PDO must complete an internal assessment. Do you want me to notify the CiC Process Owner to begin this internal assessment?' : isPPP ? 'Would you like to notify the PPP Reviewers about this request?' : isPrivacy ? 'Approval of this request is required. PDO must complete an internal privacy assessment. Do you want me to notify the Privacy Process Owner to begin this assessment?' : isSalesOps ? 'Would you like to notify the SalesOps Process Owner about this request?' : isPricingPackaging ? 'Would you like to notify the Pricing & Packaging reviewer about this request?' : undefined}
        />
      )}

      {/* EA Prefill Modal */}
      {showEAPrefillModal && eaPrefillData && (
        <EAPrefillModal
          productName={answers.gpr_ga_product_name}
          eaRequestNumber={eaPrefillData.request_number}
          skiRelease={eaPrefillData.ski_release}
          onConfirm={handleEAPrefillConfirm}
          onDismiss={handleEAPrefillDismiss}
        />
      )}

      {showGAPrefillModal && gaPrefillData && (
        <GAPrefillModal
          productName={answers.salesops_product_capability_name}
          gaRequestNumber={gaPrefillData.request_number}
          skiRelease={gaPrefillData.ski_release}
          onConfirm={handleGAPrefillConfirm}
          onDismiss={handleGAPrefillDismiss}
        />
      )}

      {/* BC Prefill Modal — shown on new CiC when a verified BC exists for this product/SKI release */}
      {showBCPrefillModal && bcPrefillData && (
        <BCPrefillModal
          productName={answers.bc_product_name}
          bcRequestNumber={bcPrefillData.request_number}
          skiRelease={bcPrefillData.ski_release}
          onConfirm={handleBCPrefillConfirm}
          onDismiss={handleBCPrefillDismiss}
        />
      )}

      {/* BC Reference Modal — shown when user clicks "View BC" banner on a CuC form */}
      {showBCRefModal && bcRefData && (
        <BCReferenceModal
          bcRequestNumber={bcRefData.request_number}
          productName={answers.product_capability_name}
          skiRelease={bcRefData.ski_release}
          bcAnswers={bcRefData.answers}
          onApplyReason={handleBCRefApplyReason}
          onClose={() => setShowBCRefModal(false)}
        />
      )}

      {/* Header */}
      <div className="intake-wizard__header">
        <div>
          <h1 className="intake-wizard__title">
            {requestId ? 'Edit Request' : 'New Intake Request'}
          </h1>
          <p className="intake-wizard__subtitle">
            {getRequestTypeDisplayName()}
          </p>
          {currentProductName && (
            <p className="intake-wizard__product-name">
              {currentProductName}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          {isNPR && requestId && initialData?.status === 'Draft' && (
            <button
              className="intake-wizard__button intake-wizard__button--danger"
              onClick={handleDeleteNPR}
              disabled={loading}
            >
              Delete Request
            </button>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <button
              className="intake-wizard__cancel"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            {requestId && initialData?.status && (
              <span className={`intake-wizard__status-badge intake-wizard__status-badge--${initialData.status.toLowerCase().replace(/\s+/g, '-')}`}>
                {initialData.status}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* CiC auto-created banner — shown on verified BC requests */}
      {isBreakingChange && initialData?.linked_cic_request_id && (
        <div className="intake-wizard__cic-created-banner">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>
            A CiC request was automatically created when this BC was verified.{' '}
            <a
              href={`/intake/${initialData.linked_cic_request_id}`}
              className="intake-wizard__cic-created-link"
            >
              View CiC Request →
            </a>
          </span>
        </div>
      )}

      {/* Email already sent banner */}
      {lastEmailSent && (isBreakingChange || isCIC || isPPP || isPrivacy || isSalesOps || isGPRSubType) && (
        <div className="intake-wizard__email-sent-banner">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
          <span>
            A notification email was sent for this request on{' '}
            <strong>{new Date(lastEmailSent.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>.
            {' '}
            {!isGPRSubType && (
              <button
                className="intake-wizard__email-resend-link"
                onClick={() => setShowBCModal(true)}
                type="button"
              >
                Re-send notification
              </button>
            )}
          </span>
        </div>
      )}

      {/* BC Reference Banner — shown on CuC when a verified BC exists for this product/SKI release */}
      {isCUC && showBCRefBanner && bcRefData && (
        <div className="intake-wizard__bc-banner">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm0 2a1 1 0 00-1 1v4a1 1 0 102 0v-4a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>
            A verified Breaking Change (<strong>{bcRefData.request_number}</strong>) exists for this product and SKI release.{' '}
            <button
              className="intake-wizard__bc-banner-link"
              type="button"
              onClick={() => setShowBCRefModal(true)}
            >
              View BC Details →
            </button>
          </span>
        </div>
      )}

      {/* Progress Indicator */}
      <ProgressIndicator
        steps={steps}
        currentStep={currentStep}
        completionPercentage={completionPercentage}
        onStepClick={handleStepClick}
        visibleStepIndices={visibleStepIndices}
      />

      {/* Error Message */}
      {error && (
        <div className="intake-wizard__error">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Wizard Step */}
      <div className="intake-wizard__content" ref={contentRef}>
        <WizardStep
          step={currentStep}
          stepTitle={steps[currentStep].title}
          questions={visibleQuestions}
          answers={answers}
          requestData={requestData}
          users={users}
          products={products}
          skiReleases={skiReleases}
          onAnswerChange={handleAnswerChange}
          onRequestDataChange={handleRequestDataChange}
          isEditMode={!!requestId}
          formType={effectiveTypeCode}
        />
      </div>

      {/* Footer Actions */}
      <div className="intake-wizard__footer">
        <div className="intake-wizard__footer-left">
          <button
            className="intake-wizard__button intake-wizard__button--secondary"
            onClick={handleSaveDraft}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save as Draft'}
          </button>
        </div>

        <div className="intake-wizard__footer-right">
          {currentStep > 0 && (
            <button
              className="intake-wizard__button intake-wizard__button--secondary"
              onClick={handleBack}
              disabled={loading}
            >
              Back
            </button>
          )}

          {hasMoreVisibleSteps ? (
            <button
              className="intake-wizard__button intake-wizard__button--primary"
              onClick={handleNext}
              disabled={loading}
            >
              Next
            </button>
          ) : isNPR ? (
            <button
              className="intake-wizard__button intake-wizard__button--primary"
              onClick={handleNPRSubmit}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          ) : requestId && canVerify ? (
            <button
              className="intake-wizard__button intake-wizard__button--primary"
              onClick={handleSaveVerified}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save as Verified'}
            </button>
          ) : (
            null
          )}
        </div>
      </div>
    </div>
  );
};

IntakeWizard.propTypes = {
  requestId: PropTypes.string,
  requestType: PropTypes.string,
  gprSubtype: PropTypes.string,
  initialData: PropTypes.object,
  questions: PropTypes.array.isRequired,
  currentUser: PropTypes.object,
};

export default IntakeWizard;
